// Formulários de satisfação — um por cliente, em nome do consultor que representa esse cliente.
// Negócio partilhado na equipa: cada consultor só vê e envia o do seu cliente (RLS em
// formularios_satisfacao e leads). Negócio com agência externa: só o lado da equipa.
import { useState, useEffect, useMemo } from "react";
import { Eye, Send, Check } from "lucide-react";
import { paleta } from "./tokens";

const NAVY = "#12244f";
export const LINK_BASE = "https://equipasoniacunha.pt/satisfacao/";
const ROTULO = { comprador: "Comprador", vendedor: "Vendedor", arrendatario: "Arrendatário", senhorio: "Senhorio" };
export const negocioConcluido = (n) => ["escriturado", "vendido", "arrendado", "fechado"].includes(n?.estado) && !n?.deleted_at; // igual ao separador Concluídos

// Quem representa cada cliente do negócio. Fonte principal: a proposta
// (consultor_ang_tipo / consultor_venda_tipo = "interno" → da equipa; "outra_marca"/"remax_ext" → outra agência).
// Os campos do negócio estão muitas vezes incompletos (nome sem ligação ao perfil), por isso o consultor
// de cada lado é procurado por esta ordem: id na proposta → id no negócio → nome → dono da ficha do cliente.
// ctx = { propostas, consultores, leads }
const norm = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
export const ladosComMotivo = (n, ctx = {}) => {
  if (!n) return [];
  const p = (ctx.propostas || []).find(x => x.id === n.proposta_id) || null;
  const porNome = (nome) => nome ? (ctx.consultores || []).find(c => norm(c.nome) === norm(nome)) : null;
  const donoLead = (id) => (ctx.leads || []).find(l => String(l.id) === String(id))?.consultor_id || null;
  const arr = n.tipo === "arrendamento";
  const lado = (papel, leadId, tipo, idProp, nomeProp, idNeg, nomeNeg, extAgencia) => {
    if (!leadId) return null;
    let daEquipa, motivo = "";
    if (tipo) { daEquipa = tipo === "interno"; if (!daEquipa) motivo = `cliente de outra agência${extAgencia ? " (" + extAgencia + ")" : ""}`; }
    else daEquipa = !n.externo || !!idNeg || !!idProp; // sem indicação na proposta
    if (!daEquipa) return { papel, leadId, consultorId: null, consultorNome: "", disponivel: false, motivo: motivo || "cliente de outra agência" };
    const c = (idProp && { id: idProp, nome: nomeProp }) || (idNeg && { id: idNeg, nome: nomeNeg })
      || porNome(nomeProp) || porNome(nomeNeg) || (donoLead(leadId) && { id: donoLead(leadId) });
    const id = c?.id || null;
    const nome = c?.nome || (ctx.consultores || []).find(x => x.id === id)?.nome || nomeProp || nomeNeg || "";
    return { papel, leadId, consultorId: id, consultorNome: nome, disponivel: !!id, motivo: id ? "" : "sem consultor da equipa identificado" };
  };
  const comprador = lado(arr ? "arrendatario" : "comprador", n.lead_comprador_id, p?.consultor_venda_tipo,
    p?.consultor_venda_tipo === "interno" ? p?.consultor_venda_id : null, p?.consultor_venda_nome, n.consultor_venda_id, n.consultor_venda_nome, p?.comprador_ext_agencia || p?.consultor_venda_ext_imobiliaria);
  const vendedor = lado(arr ? "senhorio" : "vendedor", n.lead_vendedor_id, p?.consultor_ang_tipo,
    p?.consultor_ang_tipo === "interno" ? p?.consultor_ang_id : null, p?.consultor_ang_nome, n.consultor_ang_id, n.consultor_ang_nome, p?.imovel_ext_agencia || p?.consultor_ang_ext_imobiliaria || n.externo_agencia);
  // Um só consultor da equipa num negócio interno: se um lado não tem consultor identificado, é o mesmo do outro lado
  if (!n.externo && !p?.consultor_venda_tipo && comprador && !comprador.consultorId && vendedor?.consultorId) Object.assign(comprador, { consultorId: vendedor.consultorId, consultorNome: vendedor.consultorNome, disponivel: true, motivo: "" });
  if (!n.externo && !p?.consultor_ang_tipo && vendedor && !vendedor.consultorId && comprador?.consultorId) Object.assign(vendedor, { consultorId: comprador.consultorId, consultorNome: comprador.consultorNome, disponivel: true, motivo: "" });
  return [comprador, vendedor].filter(Boolean);
};
// Só os clientes da equipa (com consultor identificado)
export const ladosDoNegocio = (n, ctx = {}) => ladosComMotivo(n, ctx).filter(l => l.disponivel).map(({ disponivel, motivo, ...l }) => l);

export const papelDoFormulario = (f, n) => {
  if (f?.papel_cliente) return f.papel_cliente;
  if (!n) return "";
  const arr = n.tipo === "arrendamento";
  if (String(f.lead_id) === String(n.lead_comprador_id)) return arr ? "arrendatario" : "comprador";
  if (String(f.lead_id) === String(n.lead_vendedor_id)) return arr ? "senhorio" : "vendedor";
  return "";
};

export const mensagemModelo = (papel, nomeCliente, consultorNome, link) => {
  const nome = String(nomeCliente || "").trim().split(/\s+/)[0] || "";
  const oque = {
    comprador: "na compra da sua nova casa. 🏡",
    vendedor: "na venda do seu imóvel. 🤝",
    arrendatario: "no arrendamento da sua nova casa. 🏡",
    senhorio: "no arrendamento do seu imóvel. 🤝",
  }[papel] || "no seu processo. 🏡";
  return `Olá${nome ? " " + nome : ""}! 👋\n\nFoi um prazer acompanhá-lo(a) ${oque}\n\nA sua opinião é muito importante para nós. Pode responder a este breve questionário (2 minutos)?\n\n${link}\n\nObrigado(a)!\n${consultorNome || ""}\nEquipa Sónia Cunha · RE/MAX White`;
};

const estadoDo = (f) => f?.preenchido_em ? "respondido" : f?.enviado_em ? "enviado" : "por_enviar";
const ESTADO_UI = {
  por_enviar: { l: "Por enviar", cor: paleta.navy900, bg: paleta.navy100 },
  enviado: { l: "Enviado", cor: paleta.aviso, bg: paleta.avisoFundo },
  respondido: { l: "Respondido", cor: paleta.sucesso, bg: paleta.sucessoFundo },
};
const fmtData = (d) => d ? new Date(d).toLocaleDateString("pt-PT") : "";
const descNegocio = (n) => [n?.imovel_ref, n?.imovel_tipologia, n?.imovel_morada].filter(Boolean).join(" · ") || "Negócio";

const btn = (bg, cor, borda) => ({ background: bg, color: cor, border: borda ? `1.5px solid ${borda}` : "none", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer" });
const lbl = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", display: "block", marginBottom: 5 };
const campo = { width: "100%", padding: "8px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 13, background: "#fff", fontFamily: "inherit" };

function Modal({ titulo, onFechar, children, largura = 560 }) {
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 3000, overflowY: "auto", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, maxWidth: largura, margin: "30px auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{titulo}</div>
          <button onClick={onFechar} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Pesquisa de negócio por ID do imóvel, morada, cliente ou consultor
const nomeLead = (leads, id) => { const l = (leads || []).find(x => String(x.id) === String(id)); return l?.nome || l?.nome_completo || ""; };
const textoNegocio = (n, leads) => [n.imovel_ref, n.referencia, n.imovel_tipologia, n.imovel_tipo, n.imovel_morada, n.consultor_ang_nome, n.consultor_venda_nome, nomeLead(leads, n.lead_comprador_id), nomeLead(leads, n.lead_vendedor_id)].filter(Boolean).join(" ");
function PesquisaNegocio({ lista, leads, valor, onEscolher }) {
  const [q, setQ] = useState("");
  const escolhido = lista.find(n => n.id === valor);
  if (escolhido) return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1.5px solid #c7d2fe", borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>🏠 {escolhido.imovel_ref || "Sem ID"} · {[escolhido.imovel_tipologia, escolhido.imovel_morada].filter(Boolean).join(" · ")}</div>
        <div style={{ fontSize: 11.5, color: "#6b7280" }}>{[nomeLead(leads, escolhido.lead_comprador_id) && "Comprador: " + nomeLead(leads, escolhido.lead_comprador_id), nomeLead(leads, escolhido.lead_vendedor_id) && "Vendedor: " + nomeLead(leads, escolhido.lead_vendedor_id), fmtData(escolhido.data_escritura_realizada)].filter(Boolean).join(" · ")}</div>
      </div>
      <button onClick={() => onEscolher("")} style={{ background: "none", border: "none", color: NAVY, fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>Trocar</button>
    </div>
  );
  const termo = q.trim().toLowerCase();
  const res = lista.filter(n => !termo || textoNegocio(n, leads).toLowerCase().includes(termo)).slice(0, 30);
  return (
    <div>
      <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Pesquisar por ID do imóvel, morada, cliente ou consultor…" style={campo} />
      <div style={{ maxHeight: 240, overflowY: "auto", border: "1.5px solid #ece9e3", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
        {res.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: "#9ca3af" }}>Nenhum negócio concluído encontrado.</div>}
        {res.map(n => (
          <div key={n.id} onClick={() => onEscolher(n.id)} style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", cursor: "pointer" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{n.imovel_ref || "Sem ID"} · {[n.imovel_tipologia, n.imovel_morada].filter(Boolean).join(" · ") || "—"}</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>{[nomeLead(leads, n.lead_comprador_id), nomeLead(leads, n.lead_vendedor_id), n.consultor_venda_nome || n.consultor_ang_nome, fmtData(n.data_escritura_realizada)].filter(Boolean).join(" · ")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Negócios concluídos onde a pessoa representa pelo menos um cliente (líder/admin: todos)
const negociosDaPessoa = (negocios, userId, isLider, ctx) => (negocios || [])
  .filter(negocioConcluido)
  .filter(n => isLider || ladosDoNegocio(n, ctx).some(l => l.consultorId === userId))
  .sort((a, b) => String(b.data_escritura_realizada || b.updated_at || "").localeCompare(String(a.data_escritura_realizada || a.updated_at || "")));

async function criarFormulario(supabase, n, lado, profileId) {
  const { data, error } = await supabase.from("formularios_satisfacao").insert({
    negocio_id: n.id, lead_id: lado.leadId, consultor_id: lado.consultorId, consultor_nome: lado.consultorNome, created_by: profileId,
  }).select().single();
  if (error) throw error;
  return data;
}

// Cartão de um formulário criado: referência, cliente, link, ver o que o cliente vê
export function CartaoFormulario({ f, leads, papel, onEnviar }) {
  const [copiado, setCopiado] = useState(false);
  const link = LINK_BASE + f.token;
  const e = ESTADO_UI[estadoDo(f)];
  return (
    <div style={{ border: "1px solid " + paleta.cinza200, background: "#fff", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: paleta.navy900 }}>{ROTULO[papel] || "Cliente"} · {nomeLead(leads, f.lead_id) || "cliente"}</div>
        <span style={{ background: e.bg, color: e.cor, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{e.l}{f.enviado_em ? ` · ${fmtData(f.enviado_em)}${f.enviado_via ? " · " + f.enviado_via : ""}` : ""}</span>
      </div>
      <div style={{ fontSize: 12.5, color: paleta.cinza600, margin: "3px 0 10px" }}>Ref. {f.referencia || "—"} · Consultor: {f.consultor_nome || "—"}</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", background: paleta.navy50, border: "1px solid " + paleta.cinza200, borderRadius: 8, padding: "6px 10px", fontSize: 12.5, color: paleta.cinza900, marginBottom: 10, overflow: "hidden" }}>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{link}</span>
        <button onClick={async () => { try { await navigator.clipboard.writeText(link); setCopiado(true); setTimeout(() => setCopiado(false), 1500); } catch { /* sem acesso */ } }} style={{ background: "none", border: "none", color: NAVY, fontWeight: 600, cursor: "pointer", fontSize: 12.5 }}>{copiado ? "Copiado" : "Copiar"}</button>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <a href={link} target="_blank" rel="noreferrer" style={{ ...btn("#fff", NAVY, paleta.cinza200), textDecoration: "none", padding: "6px 12px", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}><Eye size={15} aria-hidden="true" />Ver o formulário</a>
        {!f.preenchido_em && onEnviar && <button onClick={() => onEnviar(f)} style={{ ...btn(NAVY, "#fff"), padding: "6px 12px", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}><Send size={14} aria-hidden="true" />{f.enviado_em ? "Reenviar" : "Enviar form"}</button>}
        {f.preenchido_em && <span style={{ fontSize: 13, color: paleta.sucesso, fontWeight: 600, alignSelf: "center", display: "inline-flex", alignItems: "center", gap: 6 }}><Check size={15} aria-hidden="true" />Respondido a {fmtData(f.preenchido_em)} · {"★".repeat(f.avaliacao_geral || 0)}</span>}
      </div>
    </div>
  );
}

// ── Janela "📨 Enviar formulário": 1 escrever → 2 confirmar ───────────
export function JanelaEnviarFormulario({ supabase, negocios, leads, propostas, consultores, formularios, userId, profile, isLider, negocioInicial, formularioInicial, onFechar, onRefresh, showNotif }) {
  const ctx = { propostas, consultores, leads };
  const lista = useMemo(() => negociosDaPessoa(negocios, userId, isLider, { propostas, consultores, leads }), [negocios, userId, isLider, propostas, consultores, leads]);
  const [negocioId, setNegocioId] = useState(negocioInicial?.id || formularioInicial?.negocio_id || "");
  const n = (negocios || []).find(x => x.id === negocioId);
  const lados = ladosDoNegocio(n, ctx).filter(l => isLider || l.consultorId === userId);
  const indisponiveis = ladosComMotivo(n, ctx).filter(l => !lados.some(x => String(x.leadId) === String(l.leadId)));
  const [leadId, setLeadId] = useState(formularioInicial?.lead_id ? String(formularioInicial.lead_id) : "");
  const lado = lados.find(l => String(l.leadId) === String(leadId));
  const lead = (leads || []).find(l => String(l.id) === String(leadId));
  const [formCriado, setFormCriado] = useState(null);
  const form = formCriado || (formularios || []).find(f => f.negocio_id === negocioId && String(f.lead_id) === String(leadId));
  const [canal, setCanal] = useState("whatsapp");
  const [mensagem, setMensagem] = useState("");
  const [editada, setEditada] = useState(false);
  const [passo, setPasso] = useState(1);
  const [aEnviar, setAEnviar] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    setFormCriado(null); setPasso(1);
    if (lados.length === 1 && String(lados[0].leadId) !== String(leadId)) setLeadId(String(lados[0].leadId));
    if (lados.length === 0 && leadId) setLeadId("");
  }, [negocioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = form?.token ? LINK_BASE + form.token : LINK_BASE + "…";
  useEffect(() => {
    if (editada || !lado) return;
    setMensagem(mensagemModelo(lado.papel, lead?.nome_completo || lead?.nome, lado.consultorNome, link));
  }, [leadId, negocioId, form?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  const temTel = !!(lead?.telefone || "").replace(/\D/g, "");
  const temEmail = /\S+@\S+\.\S+/.test(lead?.email || "");
  useEffect(() => { if (canal === "whatsapp" && !temTel && temEmail) setCanal("email"); }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  const comLink = (texto, f) => {
    const real = LINK_BASE + f.token;
    return texto.includes(LINK_BASE) ? texto.replace(/https:\/\/equipasoniacunha\.pt\/satisfacao\/\S*/g, real) : texto + "\n\n" + real;
  };

  // Passo 1 → 2: cria o formulário (se ainda não existe) para mostrar o link real antes de enviar
  const preVisualizar = async () => {
    setErro("");
    if (!lado) { setErro("Escolhe o cliente."); return; }
    try {
      let f = form;
      if (!f) { f = await criarFormulario(supabase, n, lado, profile?.id); setFormCriado(f); onRefresh && onRefresh(); }
      setMensagem(m => comLink(m, f));
      setPasso(2);
    } catch (e) { setErro(e.message || "Erro ao criar o formulário"); }
  };

  const confirmarEnvio = async () => {
    setErro("");
    if (form?.preenchido_em && !window.confirm("Este cliente já respondeu. Enviar outra vez?")) return;
    setAEnviar(true);
    try {
      const f = form;
      const texto = comLink(mensagem, f);
      if (canal === "whatsapp") {
        const tel = lead.telefone.replace(/\D/g, "");
        window.open(`https://wa.me/${tel.startsWith("351") || tel.length > 9 ? tel : "351" + tel}?text=${encodeURIComponent(texto)}`, "_blank");
      } else if (canal === "email") {
        const { data: s } = await supabase.auth.getSession();
        const r = await fetch("/api/email-satisfacao", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${s?.session?.access_token || ""}` }, body: JSON.stringify({ formulario_id: f.id, mensagem: texto }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || "Erro ao enviar o email");
      } else {
        await navigator.clipboard.writeText(texto);
      }
      if (canal !== "email") await supabase.from("formularios_satisfacao").update({ enviado_em: new Date().toISOString(), enviado_via: canal === "whatsapp" ? "whatsapp" : "outro" }).eq("id", f.id);
      await supabase.from("historico").insert({
        lead_id: lado.leadId, user_id: profile?.id || null, user_nome: profile?.nome || "", tipo: "satisfacao",
        descricao: `📨 Formulário de satisfação (Ref. ${f.referencia || f.id}) enviado por ${canal === "whatsapp" ? "WhatsApp" : canal === "email" ? "email" : "outro meio (link copiado)"}`,
        referencia_tabela: "negocios", referencia_id: n.id,
      });
      showNotif && showNotif(canal === "email" ? "📧 Email enviado · registado" : canal === "whatsapp" ? "WhatsApp aberto · envio registado" : "📋 Mensagem copiada · envio registado");
      onRefresh && onRefresh();
      onFechar();
    } catch (e) { setErro(e.message || "Erro ao enviar"); }
    setAEnviar(false);
  };

  const canalBtn = (k, l, ok, porque) => (
    <button key={k} onClick={() => ok && setCanal(k)} disabled={!ok} title={ok ? "" : porque}
      style={{ ...btn(canal === k && ok ? NAVY : "#fff", !ok ? "#c0c4cc" : canal === k ? "#fff" : "#374151", canal === k && ok ? null : "#ece9e3"), fontWeight: 600, cursor: ok ? "pointer" : "not-allowed" }}>{l}</button>
  );
  const destino = canal === "whatsapp" ? `WhatsApp · ${lead?.telefone || ""}` : canal === "email" ? `Email · ${lead?.email || ""} (de satisfacao@equipasoniacunha.pt)` : "Copiar a mensagem para colar noutro meio";

  return (
    <Modal titulo={passo === 1 ? "📨 Enviar formulário de satisfação" : "✅ Confirmar envio"} onFechar={onFechar} largura={600}>
      {passo === 1 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <span style={lbl}>1 · Negócio</span>
            <PesquisaNegocio lista={lista} leads={leads} valor={negocioId} onEscolher={(id) => { setNegocioId(id); setEditada(false); }} />
          </div>
          {n && (
            <div>
              <span style={lbl}>2 · Cliente</span>
              {lados.length === 0
                ? <div style={{ fontSize: 12.5, color: "#9ca3af" }}>Não representas nenhum cliente deste negócio.</div>
                : <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {lados.map(l => {
                      const fx = (formularios || []).find(f => f.negocio_id === n.id && String(f.lead_id) === String(l.leadId));
                      const on = String(leadId) === String(l.leadId);
                      return <button key={l.leadId} onClick={() => { setLeadId(String(l.leadId)); setEditada(false); setFormCriado(null); }} style={{ ...btn(on ? NAVY : "#fff", on ? "#fff" : "#374151", on ? null : "#ece9e3"), fontWeight: 600, textAlign: "left" }}>
                        {ROTULO[l.papel]} · {nomeLead(leads, l.leadId) || "cliente"}<span style={{ opacity: 0.75, fontWeight: 400 }}> · {fx ? ESTADO_UI[estadoDo(fx)].l : "sem formulário"}</span>
                      </button>;
                    })}
                  </div>}
              {indisponiveis.map(l => <div key={"x" + l.leadId} style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 6 }}>🔒 {ROTULO[l.papel]}{nomeLead(leads, l.leadId) ? " · " + nomeLead(leads, l.leadId) : ""}: {l.motivo || (l.consultorNome ? "cliente de " + l.consultorNome : "cliente de colega")} — não disponível</div>)}
            </div>
          )}
          {lado && (
            <>
              <div>
                <span style={lbl}>3 · Enviar por</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canalBtn("whatsapp", "💬 WhatsApp", temTel, "O cliente não tem telefone na ficha")}
                  {canalBtn("email", "📧 Email", temEmail, "O cliente não tem email na ficha")}
                  {canalBtn("outro", "📋 Outro (copiar)", true)}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{destino}</div>
              </div>
              <div>
                <span style={lbl}>4 · Mensagem ({ROTULO[lado.papel]?.toLowerCase()})</span>
                <textarea value={mensagem} onChange={e => { setMensagem(e.target.value); setEditada(true); }} rows={9} style={{ ...campo, resize: "vertical", lineHeight: 1.45 }} />
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{form ? `Ref. ${form.referencia || ""} · ` : "O formulário é criado no passo seguinte · "}O link pessoal do cliente entra sempre na mensagem.</div>
              </div>
            </>
          )}
          {erro && <div style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 600 }}>⚠️ {erro}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onFechar} style={btn("#fff", "#6b7280", "#ece9e3")}>Cancelar</button>
            <button onClick={preVisualizar} disabled={!lado} style={btn(lado ? NAVY : "#9ca3af", "#fff")}>Seguinte · Ver antes de enviar →</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {form && <CartaoFormulario f={form} leads={leads} papel={lado?.papel} />}
          <div>
            <span style={lbl}>Para</span>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{nomeLead(leads, leadId)} · {destino}</div>
          </div>
          <div>
            <span style={lbl}>Mensagem que vai ser enviada (ainda podes editar)</span>
            <textarea value={mensagem} onChange={e => setMensagem(e.target.value)} rows={11} style={{ ...campo, resize: "vertical", lineHeight: 1.45, background: "#fffdf5", borderColor: "#fde68a" }} />
          </div>
          {erro && <div style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 600 }}>⚠️ {erro}</div>}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setPasso(1)} style={btn("#fff", "#6b7280", "#ece9e3")}>← Voltar</button>
            <button onClick={confirmarEnvio} disabled={aEnviar} style={btn("#16a34a", "#fff")}>{aEnviar ? "A enviar…" : canal === "outro" ? "✅ Confirmar · copiar mensagem" : canal === "email" ? "✅ Confirmar envio do email" : "✅ Confirmar · abrir WhatsApp"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── "➕ Criar formulário" (manual): negócio → comprador / vendedor / ambos → mostra o criado ──
export function CriarFormulario({ supabase, negocios, leads, propostas, consultores, formularios, userId, profile, isLider, negocioInicial, papeisIniciais, onFechar, onRefresh, onEnviar, showNotif }) {
  const ctx = { propostas, consultores, leads };
  const lista = useMemo(() => negociosDaPessoa(negocios, userId, isLider, { propostas, consultores, leads }), [negocios, userId, isLider, propostas, consultores, leads]);
  const [negocioId, setNegocioId] = useState(negocioInicial?.id || "");
  const n = (negocios || []).find(x => x.id === negocioId);
  const lados = ladosDoNegocio(n, ctx).filter(l => isLider || l.consultorId === userId);
  const indisponiveis = ladosComMotivo(n, ctx).filter(l => !lados.some(x => String(x.leadId) === String(l.leadId)));
  const existe = (l) => (formularios || []).find(f => f.negocio_id === negocioId && String(f.lead_id) === String(l.leadId));
  const novos = lados.filter(l => !existe(l));
  const [escolha, setEscolha] = useState("");
  const [aCriar, setACriar] = useState(false);
  const [criados, setCriados] = useState([]);
  const [erro, setErro] = useState("");
  useEffect(() => {
    const pedidos = (papeisIniciais || []).filter(p => novos.some(l => l.papel === p));
    setEscolha(pedidos.length === 1 ? pedidos[0] : novos.length > 1 ? "ambos" : novos[0]?.papel || "");
    setCriados([]);
  }, [negocioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const criar = async () => {
    const alvo = escolha === "ambos" ? novos : novos.filter(l => l.papel === escolha);
    if (!alvo.length) return;
    setACriar(true); setErro("");
    try {
      const feitos = [];
      for (const l of alvo) feitos.push({ f: await criarFormulario(supabase, n, l, profile?.id), papel: l.papel });
      setCriados(feitos);
      showNotif && showNotif(`✅ ${feitos.length} formulário(s) criado(s)`);
      onRefresh && onRefresh();
    } catch (e) { setErro(e.message || "Erro ao criar"); }
    setACriar(false);
  };

  const opc = (k, l) => <button key={k} onClick={() => setEscolha(k)} style={{ ...btn(escolha === k ? NAVY : "#fff", escolha === k ? "#fff" : "#374151", escolha === k ? null : "#ece9e3"), fontWeight: 600 }}>{l}</button>;
  return (
    <Modal titulo="➕ Criar formulário de satisfação" onFechar={onFechar} largura={580}>
      {criados.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 13, color: "#15803d", fontWeight: 700 }}>✅ Formulário(s) criado(s). Ainda não foram enviados.</div>
          {criados.map(({ f, papel }) => <CartaoFormulario key={f.id} f={f} leads={leads} papel={papel} onEnviar={(fx) => onEnviar(n, fx)} />)}
          <div style={{ display: "flex", justifyContent: "flex-end" }}><button onClick={onFechar} style={btn("#fff", "#6b7280", "#ece9e3")}>Fechar</button></div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <span style={lbl}>Negócio</span>
            <PesquisaNegocio lista={lista} leads={leads} valor={negocioId} onEscolher={setNegocioId} />
          </div>
          {n && (
            <div>
              <span style={lbl}>Para quem</span>
              {lados.length === 0 && <div style={{ fontSize: 12.5, color: "#9ca3af" }}>{!n.lead_comprador_id && !n.lead_vendedor_id ? "Este negócio não tem clientes ligados. Liga o comprador/vendedor na ficha do negócio." : `Não representas nenhum cliente deste negócio${n.externo ? " (o outro lado é de outra agência)" : ""}.`}</div>}
              {lados.filter(existe).map(l => <div key={l.leadId} style={{ marginBottom: 8 }}><div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 4 }}>Já existe:</div><CartaoFormulario f={existe(l)} leads={leads} papel={l.papel} onEnviar={(fx) => onEnviar(n, fx)} /></div>)}
              {novos.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {novos.map(l => opc(l.papel, `${ROTULO[l.papel]} · ${nomeLead(leads, l.leadId) || "cliente"}`))}
                  {novos.length > 1 && opc("ambos", "Ambos")}
                </div>
              )}
              {indisponiveis.map(l => <div key={"x" + l.leadId} style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 6 }}>🔒 {ROTULO[l.papel]}{nomeLead(leads, l.leadId) ? " · " + nomeLead(leads, l.leadId) : ""}: {l.motivo || (l.consultorNome ? "cliente de " + l.consultorNome : "cliente de colega")} — não disponível</div>)}
            </div>
          )}
          {erro && <div style={{ fontSize: 12.5, color: "#dc2626", fontWeight: 600 }}>⚠️ {erro}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onFechar} style={btn("#fff", "#6b7280", "#ece9e3")}>Cancelar</button>
            <button onClick={criar} disabled={!escolha || aCriar || !novos.length} style={btn(escolha && novos.length ? NAVY : "#9ca3af", "#fff")}>{aCriar ? "A criar…" : "➕ Criar"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Bloco "⭐ Satisfação" dentro do processo do negócio ─────────────────
export function SatisfacaoDoNegocio({ negocio, leads, propostas, consultores, formularios, userId, isLider, onCriar, onEnviar }) {
  const ctx = { propostas, consultores, leads };
  const lados = ladosDoNegocio(negocio, ctx).filter(l => isLider || l.consultorId === userId);
  const indisponiveis = ladosComMotivo(negocio, ctx).filter(l => !lados.some(x => String(x.leadId) === String(l.leadId)));
  const forms = lados.map(l => ({ l, f: (formularios || []).find(f => f.negocio_id === negocio.id && String(f.lead_id) === String(l.leadId)) }));
  const semForm = forms.filter(x => !x.f);
  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", marginBottom: 8 }}>─── ⭐ SATISFAÇÃO ───</div>
      {lados.length === 0 && <div style={{ fontSize: 12, color: "#9ca3af" }}>{!negocio.lead_comprador_id && !negocio.lead_vendedor_id ? "Negócio sem clientes ligados." : "Não representas nenhum cliente deste negócio."}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {forms.filter(x => x.f).map(({ l, f }) => <CartaoFormulario key={f.id} f={f} leads={leads} papel={l.papel} onEnviar={(fx) => onEnviar(negocio, fx)} />)}
      </div>
      {indisponiveis.map(l => <div key={"x" + l.leadId} style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 6 }}>🔒 {ROTULO[l.papel]}{nomeLead(leads, l.leadId) ? " · " + nomeLead(leads, l.leadId) : ""}: {l.motivo || (l.consultorNome ? "cliente de " + l.consultorNome : "cliente de colega")} — não disponível</div>)}
      {semForm.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>Criar formulário:</span>
          {semForm.map(({ l }) => <button key={l.leadId} onClick={() => onCriar(negocio, [l.papel])} style={{ ...btn("#fff", NAVY, "#c7d2fe"), padding: "6px 12px", fontSize: 12 }}>➕ {ROTULO[l.papel]}</button>)}
          {semForm.length > 1 && <button onClick={() => onCriar(negocio, semForm.map(x => x.l.papel))} style={{ ...btn(NAVY, "#fff"), padding: "6px 12px", fontSize: 12 }}>➕ Ambos</button>}
        </div>
      )}
    </div>
  );
}

// ── Separador "📨 Formulários" ────────────────────────────────────────
export function ListaFormularios({ formularios, negocios, leads, propostas, consultores, isLider, onEnviar, onCriar }) {
  const [estado, setEstado] = useState("");
  const [cons, setCons] = useState("");
  const [pesquisa, setPesquisa] = useState("");
  const negDe = (f) => (negocios || []).find(n => n.id === f.negocio_id);
  const leadDe = (f) => (leads || []).find(l => String(l.id) === String(f.lead_id));
  const todos = formularios || [];
  const contar = (e) => todos.filter(f => estadoDo(f) === e).length;
  const q = pesquisa.trim().toLowerCase();
  const linhas = todos
    .filter(f => !estado || estadoDo(f) === estado)
    .filter(f => !cons || f.consultor_id === cons)
    .filter(f => !q || [f.referencia, leadDe(f)?.nome, f.consultor_nome, descNegocio(negDe(f))].join(" ").toLowerCase().includes(q))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        {[["", `Todos (${todos.length})`], ["por_enviar", `Por enviar (${contar("por_enviar")})`], ["enviado", `Enviados (${contar("enviado")})`], ["respondido", `Respondidos (${contar("respondido")})`]].map(([k, l]) => (
          <button key={k} onClick={() => setEstado(k)} style={{ ...btn(estado === k ? NAVY : "#fff", estado === k ? "#fff" : "#374151", estado === k ? null : "#ece9e3"), fontWeight: 600, padding: "7px 12px", fontSize: 12 }}>{l}</button>
        ))}
        {isLider && (
          <select value={cons} onChange={e => setCons(e.target.value)} style={{ padding: "7px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 12, background: "#fff" }}>
            <option value="">Todos os consultores</option>
            {(consultores || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        )}
        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="🔍 Pesquisar ID do imóvel, ref., cliente…" style={{ padding: "7px 10px", border: "1.5px solid #ece9e3", borderRadius: 8, fontSize: 12, minWidth: 180, flex: "1 1 180px" }} />
        <button onClick={onCriar} style={btn("#fff", NAVY, "#c7d2fe")}>➕ Criar formulário</button>
        <button onClick={() => onEnviar(null, null)} style={btn("#16a34a", "#fff")}>📱 Enviar Form</button>
      </div>
      {linhas.length === 0 && <div style={{ background: "#fff", border: "1.5px dashed #ece9e3", borderRadius: 12, padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>Sem formulários nesta lista.</div>}
      <div style={{ display: "grid", gap: 8 }}>
        {linhas.map(f => {
          const n = negDe(f); const e = ESTADO_UI[estadoDo(f)]; const papel = papelDoFormulario(f, n);
          const ladoF = ladosComMotivo(n, { propostas, consultores, leads }).find(l => String(l.leadId) === String(f.lead_id));
          const bloqueado = ladoF && !ladoF.disponivel;
          return (
            <div key={f.id} style={{ background: "#fff", border: "1.5px solid #ece9e3", borderRadius: 10, padding: "11px 14px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{leadDe(f)?.nome || "Cliente"} <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280" }}>{ROTULO[papel] || ""}</span></div>
                <div style={{ fontSize: 11.5, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Ref. {f.referencia || "—"} · {descNegocio(n)}{isLider ? ` · ${f.consultor_nome || ""}` : ""}</div>
              </div>
              <span style={{ background: e.bg, color: e.cor, borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
                {e.l}{f.enviado_em && !f.preenchido_em ? ` · ${fmtData(f.enviado_em)}${f.enviado_via ? " · " + f.enviado_via : ""}` : ""}{f.preenchido_em ? ` · ${"★".repeat(f.avaliacao_geral || 0)}` : ""}
              </span>
              <a href={LINK_BASE + f.token} target="_blank" rel="noreferrer" title="Ver o formulário que o cliente abre" style={{ ...btn("#fff", NAVY, "#c7d2fe"), padding: "6px 10px", fontSize: 12, textDecoration: "none" }}>👁 Ver</a>
              {bloqueado && <span title="Criado por engano: este cliente não é da equipa" style={{ background: "#f3f4f6", color: "#6b7280", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>🔒 {ladoF.motivo}</span>}
              {!f.preenchido_em && !bloqueado && <button onClick={() => onEnviar(n, f)} style={{ ...btn(f.enviado_em ? "#f97316" : "#2563eb", "#fff"), padding: "6px 12px", fontSize: 12 }}>{f.enviado_em ? "📤 Reenviar" : "📱 Enviar Form"}</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
